import { useCallback, useMemo } from 'react'
import { useFunctions } from './auth'
import { httpsCallable } from 'firebase/functions'
import { useLTIMode } from './use-lti-mode'
import { InstitutionIntegrationType } from '@breakoutlearning/firebase-repository/types'

type BasePayload = {
  action: string
  deepLinkJwt: string
}

type Item = {
  slideDeckId: string
  slideDeckName: string
  catalogId: string
  options: string | undefined
}

type MultiItemPayload = BasePayload & {
  items: Item[]
}

type SingleItemPayload = BasePayload & Item

type Payload = MultiItemPayload | SingleItemPayload
export function useLtiJwtRepack() {
  const functions = useFunctions()
  const { ltiMode, ltiOptions } = useLTIMode()

  const deepLinkJwt = useMemo(() => {
    return localStorage.getItem('deepLinkJwt')
  }, [])

  const call = useCallback(
    async (
      itemsInput: {
        slideDeckId: string
        slideDeckName: string
        catalogId: string | undefined
        options?: Record<
          string,
          | number
          | string
          | boolean
          | Record<string, number>
          | Array<Record<string, string>>
        >
      }[]
    ) => {
      if (!deepLinkJwt) {
        alert('Deep link JWT is missing')
        return
      }
      const catalogIdIsMissing = itemsInput.some((item) => !item.catalogId)
      if (catalogIdIsMissing) {
        alert('Catalog ID is missing')
        return
      }

      let data: Payload | undefined = undefined

      const useMultipleForCanvas =
        ltiMode === InstitutionIntegrationType.canvas &&
        ltiOptions?.placement === 'course_assignments_menu'

      if (useMultipleForCanvas) {
        const items = itemsInput.map((item) => {
          const { slideDeckId, slideDeckName, catalogId, options } = item
          const optionsSerialized = options
            ? btoa(JSON.stringify(options))
            : undefined
          return {
            slideDeckId,
            slideDeckName: slideDeckName,
            catalogId: catalogId!,
            options: optionsSerialized,
          }
        })
        data = {
          action: 'repackCanvasDeepLinkingJWT',
          deepLinkJwt,
          items: items,
        }
      } else if (ltiMode === InstitutionIntegrationType.d2l) {
        const items = itemsInput.map((item) => {
          const { slideDeckId, slideDeckName, catalogId, options } = item
          const optionsSerialized = options
            ? btoa(JSON.stringify(options))
            : undefined
          return {
            slideDeckId,
            slideDeckName: slideDeckName,
            catalogId: catalogId!,
            options: optionsSerialized,
          }
        })
        data = {
          action: 'repackMultipleDeepLinkingJWT',
          deepLinkJwt,
          items: items,
        }
      } else {
        const item = itemsInput[0]
        const { slideDeckId, slideDeckName, catalogId, options } = item
        const optionsSerialized = options
          ? btoa(JSON.stringify(options))
          : undefined
        data = {
          action:
            ltiMode === InstitutionIntegrationType.canvas
              ? 'repackCanvasDeepLinkingJWT'
              : 'repackDeepLinkingJWT',
          deepLinkJwt,
          slideDeckId,
          slideDeckName,
          catalogId: catalogId!,
          options: optionsSerialized,
        }
      }

      const result = await httpsCallable<
        Payload,
        {
          url: string
          jwt: string
        }
      >(
        functions,
        'ltiLoginLaunch'
      )(data)

      return result.data
    },
    [deepLinkJwt, functions, ltiMode, ltiOptions?.placement]
  )

  return call
}
